import { Helmet } from "react-helmet-async";
import { useI18n } from "@/lib/i18n";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import LogoSlider from "@/components/LogoSlider";
import ProblemSection from "@/components/ProblemSection";
import FoundersStory from "@/components/FoundersStory";
import ServicesSection from "@/components/ServicesSection";

import CasesResultsSection from "@/components/CasesResultsSection";
import ReferencesSection from "@/components/ReferencesSection";
import ClosingCtaSection from "@/components/ClosingCtaSection";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";

const Index = () => {
  const { lang } = useI18n();
  const isEn = lang === "en";
  const title = isEn
    ? "A-WAVE | Marketing that understands the tech"
    : "A-WAVE | Marketing die de techniek begrijpt";
  const description = isEn
    ? "A-WAVE builds online growth systems for technical and industrial companies: visibility, qualified leads and converting websites."
    : "A-WAVE bouwt online groeisystemen voor technische en industriële bedrijven: zichtbaarheid, kwalitatieve leads en een website die verkoopt.";
  const canonical = isEn ? "https://www.awave.pro/en" : "https://www.awave.pro/";

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        q: { nl: "Voor wie werkt A-WAVE?", en: "Who does A-WAVE work for?" },
        a: {
          nl: "Directeuren en marketingmanagers van technische bedrijven in NL, BE en DE — met focus op pompen, kleppen en systemen.",
          en: "Directors and marketing managers of technical companies in NL, BE and DE — focused on pumps, valves and systems.",
        },
      },
      {
        q: { nl: "Hoe snel zien klanten resultaat?", en: "How quickly do clients see results?" },
        a: {
          nl: "Google Ads doorgaans binnen weken, SEO 6–12 maanden (compounding), websites direct meetbaar op snelheid en conversie.",
          en: "Google Ads usually within weeks, SEO 6–12 months (compounding), websites immediately measurable on speed and conversion.",
        },
      },
      {
        q: { nl: "Hoe meten jullie resultaten?", en: "How do you measure results?" },
        a: {
          nl: "Maandrapport per dienst plus tweewekelijkse sparringcall waarin we de cijfers samen doorlopen.",
          en: "Monthly report per service plus a bi-weekly sparring call where we review the numbers together.",
        },
      },
      {
        q: { nl: "Werken jullie alleen voor industriële bedrijven?", en: "Do you only work for industrial companies?" },
        a: {
          nl: "Primair ja — onze meerwaarde is dat we de techniek begrijpen en de taal van engineers spreken.",
          en: "Primarily yes — our edge is that we understand the technology and speak the engineers' language.",
        },
      },
    ].map((f) => ({
      "@type": "Question",
      name: f.q[lang],
      acceptedAnswer: { "@type": "Answer", text: f.a[lang] },
    })),
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", "h2", "dt", "dd"],
    },
  };

  const websiteLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://www.awave.pro/#website",
    url: "https://www.awave.pro/",
    name: "A-WAVE",
    inLanguage: ["nl", "en"],
    publisher: { "@id": "https://www.awave.pro/#organization" },
  };

  const peopleLd = [
    {
      "@type": "Person",
      "@id": "https://www.awave.pro/#ron",
      name: "Ron van de Weerd",
      jobTitle: isEn ? "Founder, Technical Expertise & Network" : "Founder, Technische Expertise & Netwerk",
      worksFor: { "@id": "https://www.awave.pro/#organization" },
      sameAs: ["https://www.linkedin.com/in/ronvandeweerd/"],
    },
    {
      "@type": "Person",
      "@id": "https://www.awave.pro/#ellen",
      name: "Ellen Verhoeven",
      jobTitle: isEn ? "Digital Growth Manager (SEO, SEA, performance)" : "Digital Growth Manager (SEO, SEA, performance)",
      worksFor: { "@id": "https://www.awave.pro/#organization" },
      sameAs: ["https://www.linkedin.com/in/ellenverhoeven/"],
    },
    {
      "@type": "Person",
      "@id": "https://www.awave.pro/#larissa",
      name: "Larissa Ignacio",
      jobTitle: isEn ? "Power Networking & Events" : "Power Networking & Events",
      worksFor: { "@id": "https://www.awave.pro/#organization" },
      sameAs: ["https://www.linkedin.com/in/larissaignacio/"],
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify(websiteLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqLd)}</script>
        <script type="application/ld+json">{JSON.stringify(peopleLd)}</script>
      </Helmet>
      <div className="max-w-[960px] mx-auto bg-background">
        <Navbar />
        <HeroSection />
        <LogoSlider />
        <ProblemSection />
        <FoundersStory />
        <ServicesSection />
        
        <CasesResultsSection />
        <ReferencesSection />
        <ClosingCtaSection />
        <Footer />
        <WhatsAppButton />
      </div>
    </div>
  );
};

export default Index;
